import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { message } from '@/components/base/Notification'
import { Team } from '@/models/team'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'

import type { TeamPlan } from './CreateTeamModal'
import { getTeamPlanCheckout } from './teamPlanOrder'

interface ManagePlanCheckoutModalProps {
  open: boolean
  onClose: () => void
  onBack: () => void
  onSuccess: () => void
  team: Team
  selectedPlan: TeamPlan
  currentPlanId: string
}

export function ManagePlanCheckoutModal({
  open,
  onClose,
  onBack,
  onSuccess,
  team,
  selectedPlan,
  currentPlanId,
}: ManagePlanCheckoutModalProps) {
  const { t } = useTranslation('team')
  const userStore = useUserStore()
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const checkout = getTeamPlanCheckout({
    isExpired: team.isExpired,
    isSamePlan: selectedPlan.id === currentPlanId,
    pricePerMonth: selectedPlan.pricePerMonth,
    upgradePrice: selectedPlan.upgradePrice,
  })
  const isNeedCharge = (userStore.account.totalBonus.moneyCent ?? 0) < checkout.priceCent

  const handleConfirm = async () => {
    if (!team.order) return

    setLoading(true)
    try {
      const res = await API.updateTeamOrder({
        modify: checkout.modify,
        from_order_id: team.order.id,
        res_id: selectedPlan.id,
        qty: 1,
      })

      if (res.code !== 0) {
        message.error(t('managePlan.error'))
        return
      }

      onSuccess()
    } catch (error) {
      console.error('Failed to update plan:', error)
      message.error(t('managePlan.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleButtonClick = () => {
    if (isNeedCharge) {
      setRechargeModalOpen(true)
    } else {
      handleConfirm()
    }
  }

  const getButtonLabel = () => {
    if (loading) return t('common.loading')
    if (isNeedCharge) return t('checkout.recharge')
    return t('managePlan.changePlan')
  }

  return (
    <>
      <Modal open={open} onClose={onClose} className="w-[800px] h-[600px]">
        <Modal.Header>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={onBack}
                className="p-3 hover:bg-panel-gray-200 rounded"
                disabled={loading}
              >
                <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
              </button>
              <span className="h6">
                <Trans ns="team" i18nKey="managePlan.confirmTitle">
                  Checkout
                </Trans>
              </span>
              <span className="h5 text-tertiary">
                <Trans
                  ns="team"
                  i18nKey="createTeam.stepIndicator"
                  values={{ current: 2, total: 2 }}
                >
                  (2/2)
                </Trans>
              </span>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-2">
            {/* Table Header */}
            <div className="bg-neutral-50 rounded-lg flex items-center">
              <div className="w-[200px] h-[44px]" />
              <div className="flex-1 px-4 py-3">
                <span className="t-sm text-secondary">{t('checkout.selected')}</span>
              </div>
              <div className="flex-1 px-4 py-3 text-right">
                <span className="t-sm text-secondary">{t('checkout.price')}</span>
              </div>
            </div>

            {/* Plan Price Row */}
            <div className="flex items-center border-b border-neutral-200">
              <div className="w-[200px] p-4">
                <span className="t-sm text-primary">{t('checkout.teamPlan')}</span>
              </div>
              <div className="flex-1 p-4">
                <span className="t-sm text-primary">{selectedPlan.name}</span>
              </div>
              <div className="flex-1 p-4 text-right">
                <span className="t-sm text-primary">{formatPrice(checkout.priceCent)}</span>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <span className="h4 text-highlight">{formatPrice(checkout.priceCent)}</span>
              <span className="t-x-sb text-secondary rounded-full px-3 py-2 bg-neutral-50">
                {t('checkout.myBalance')}{' '}
                <span className="text-primary">
                  {formatPrice(userStore.account.totalBonus.moneyCent ?? 0)}
                </span>
              </span>
            </div>
            <div className="flex gap-4">
              <Button type="outline" onClick={onBack} disabled={loading}>
                {t('common.previous')}
              </Button>
              <Button type="secondary" onClick={handleButtonClick} disabled={loading}>
                {getButtonLabel()}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {rechargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setRechargeModalOpen(false)
            userStore.getUserInfo()
          }}
          onClose={() => {
            setRechargeModalOpen(false)
          }}
        />
      )}
    </>
  )
}
